import React from "react";
import { Typography } from "../../components/common";
import Loading from "../../components/common/Loading";
import { useHomeHook } from "./useHomeHook";
import { Users, Target, BookOpen, KeyRound, BarChart3, UserCog, FileDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { downloadGovernorPack } from "../../api/analyticsApi";
import { Feature, useEntitlements } from "../../context/EntitlementsContext";

const Home: React.FC = () => {
  const {
    userProfile,
    greeting,
    analytics,
    organisation,
    summaryLoading,
    organizationId,
  } = useHomeHook();
  const navigate = useNavigate();
  // WS-P: gate the compliance/analytics quick-actions so an SLT-pilot org admin
  // (Learn-app only) does not see surfaces the backend now 403s. Whole-School keeps
  // them. "Manage Users" stays ungated (SLT retains seat-management).
  const { hasFeature } = useEntitlements();
  const canViewAnalytics = hasFeature(Feature.ADVANCED_ANALYTICS);
  const canDownloadGovernorPack = hasFeature(Feature.GOVERNOR_PACK_PDF);

  const [isDownloadingPack, setIsDownloadingPack] = React.useState(false);

  const handleDownloadGovernorPack = async () => {
    if (!organizationId || isDownloadingPack) return;
    setIsDownloadingPack(true);
    try {
      await downloadGovernorPack(organizationId, "30d");
    } catch (err) {
      console.error("Failed to download governor pack:", err);
    } finally {
      setIsDownloadingPack(false);
    }
  };

  return (
    <div>
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Typography variant="h2" weight="semibold">
            {greeting}, {userProfile?.name || "Admin"}!
          </Typography>
        </div>
        <Typography
          variant="h6"
          className="!text-gray-400 !text-md"
          color="secondary"
        >
          Welcome back to your Pedagogue Admin Dashboard. Here's what's
          happening today.
        </Typography>
      </div>

      {/* Summary Cards */}
      {summaryLoading ? (
        <div className="flex justify-center items-center py-12">
          <Loading />
        </div>
      ) : (
        <>
          {analytics && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              <SummaryCard
                icon={<Users size={22} />}
                color="#8b5cf6"
                label="Active Users"
                value={`${analytics.kpis.activeUsers.mau}`}
                subtitle="Monthly active users"
              />
              <SummaryCard
                icon={<Target size={22} />}
                color="#3b82f6"
                label="Completion Rate"
                value={`${analytics.kpis.completionRatePct.toFixed(1)}%`}
                subtitle="Course completion"
              />
              <SummaryCard
                icon={<BookOpen size={22} />}
                color="#6366f1"
                label="Learning Hours"
                value={`${analytics.kpis.timeInvestedHours.total.toFixed(1)}h`}
                subtitle="Total learning time"
              />
              <SummaryCard
                icon={<KeyRound size={22} />}
                color="#f59e0b"
                label="Licence Usage"
                value={
                  organisation
                    ? `${organisation.used_licences} / ${organisation.total_licences}`
                    : "\u2014"
                }
                subtitle={
                  organisation
                    ? `${organisation.available_licences ?? organisation.total_licences - organisation.used_licences} available`
                    : "Licence data unavailable"
                }
              />
            </div>
          )}

          {/* Quick Actions */}
          <div className="mb-8">
            <Typography variant="h5" weight="semibold" className="mb-4 !text-slate-900">
              Quick actions
            </Typography>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              {canViewAnalytics && (
                <QuickActionButton
                  icon={<BarChart3 size={20} />}
                  label="View Analytics"
                  onClick={() =>
                    navigate(`/analytics/dashboard/${organizationId}`)
                  }
                />
              )}
              <QuickActionButton
                icon={<UserCog size={20} />}
                label="Manage Users"
                onClick={() =>
                  navigate(`/organisation/${organizationId}`)
                }
              />
              {canDownloadGovernorPack && (
                <QuickActionButton
                  icon={<FileDown size={20} />}
                  label={isDownloadingPack ? "Generating\u2026" : "Download Governor Pack"}
                  onClick={handleDownloadGovernorPack}
                  disabled={isDownloadingPack}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* ── Small helper components ── */

const SummaryCard: React.FC<{
  icon: React.ReactNode;
  color: string;
  label: string;
  value: string;
  subtitle: string;
}> = ({ icon, color, label, value, subtitle }) => (
  <div className="p-5 rounded-xl border border-gray-200 bg-white shadow-sm">
    <div className="flex items-center gap-3 mb-3">
      <div
        className="p-2.5 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${color}15` }}
      >
        <div style={{ color }}>{icon}</div>
      </div>
      <Typography variant="small" weight="medium" className="!text-slate-500">
        {label}
      </Typography>
    </div>
    <Typography variant="h3" weight="bold" className="mb-1 !text-black">
      {value}
    </Typography>
    <Typography variant="small" color="default" className="!text-gray-400">
      {subtitle}
    </Typography>
  </div>
);

const QuickActionButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}> = ({ icon, label, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
  >
    <div className="p-2 rounded-lg bg-slate-100 text-slate-600">{icon}</div>
    <Typography variant="p" weight="medium" className="!text-slate-700">
      {label}
    </Typography>
  </button>
);

export default Home;
